/* =========================================================
   Shared chart setup for the Admin Dashboard.
   - registers Chart.js once
   - follows the admin light/dark switch (reads data-theme on .rmadl)
   - one palette for every chart (validated for colour-blind safety,
     light + dark), always used in the same order
   ========================================================= */
import { useEffect, useState } from 'react';
import {
  Chart,
  LinearScale,
  CategoryScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';

Chart.register(LinearScale, CategoryScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, annotationPlugin);

const PALETTE = {
  light: { s1: '#2a78d6', s2: '#eb6834', s3: '#1baf7a', text: '#0A1230', muted: '#4A5878', grid: 'rgba(10, 18, 48, 0.08)', tip: '#0A1230' },
  dark: { s1: '#3987e5', s2: '#d95926', s3: '#199e70', text: '#FFFFFF', muted: '#B8C4E0', grid: 'rgba(255, 255, 255, 0.08)', tip: '#050A1C' },
};

const readDark = () => {
  if (typeof document === 'undefined') return false;
  return document.querySelector('.rmadl')?.getAttribute('data-theme') === 'dark';
};

/** Re-renders charts when the admin light/dark switch changes. */
export const useChartTheme = () => {
  const [dark, setDark] = useState(readDark);
  useEffect(() => {
    const el = document.querySelector('.rmadl');
    if (!el) return undefined;
    const obs = new MutationObserver(() => setDark(readDark()));
    obs.observe(el, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);
  return { dark, c: dark ? PALETTE.dark : PALETTE.light };
};

/** Thin bars with 4-px rounded tops. */
export const barStyle = (color) => ({
  backgroundColor: color,
  borderColor: 'transparent',
  borderWidth: 0,
  borderRadius: 4,
  borderSkipped: 'bottom',
  barPercentage: 0.8,
  categoryPercentage: 0.7,
  maxBarThickness: 28,
});

/** Common options: one y-axis, quiet grid, theme colours, index tooltip. */
export const baseOptions = (c, { money = false, integer = false, yTitle } = {}) => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: {
      position: 'top',
      align: 'end',
      labels: { color: c.muted, usePointStyle: true, pointStyle: 'rectRounded', boxWidth: 10, padding: 14, font: { size: 12, weight: '600' } },
    },
    tooltip: {
      backgroundColor: c.tip,
      titleColor: '#FFFFFF',
      bodyColor: '#FFFFFF',
      padding: 10,
      cornerRadius: 8,
      boxPadding: 4,
      callbacks: money
        ? { label: (ctx) => `${ctx.dataset.label}: $${(ctx.parsed.y ?? 0).toLocaleString()}` }
        : undefined,
    },
  },
  scales: {
    x: {
      grid: { display: false },
      border: { color: c.grid },
      ticks: { color: c.muted, font: { size: 11 }, maxRotation: 0, autoSkip: true, autoSkipPadding: 12 },
    },
    y: {
      beginAtZero: true,
      border: { display: false },
      grid: { color: c.grid },
      title: yTitle ? { display: true, text: yTitle, color: c.muted, font: { size: 11, weight: '600' } } : { display: false },
      ticks: {
        color: c.muted,
        font: { size: 11 },
        precision: integer ? 0 : undefined,
        callback: money ? (v) => `$${Number(v).toLocaleString()}` : undefined,
      },
    },
  },
});

/** Same range rule as before: keep the last N months (or all). */
export const filterDataByRange = (data, range) => {
  if (!Array.isArray(data)) return [];
  const sorted = [...data].sort((a, b) => (a.year !== b.year ? a.year - b.year : a.month - b.month));
  if (range === 'all') return sorted;
  const now = new Date();
  const cutoff = new Date(now.getFullYear(), now.getMonth());
  if (range === '3months') cutoff.setMonth(cutoff.getMonth() - 3);
  else if (range === '6months') cutoff.setMonth(cutoff.getMonth() - 6);
  else if (range === '12months') cutoff.setMonth(cutoff.getMonth() - 12);
  return sorted.filter((item) => new Date(item.year, item.month - 1) >= cutoff);
};

export const monthLabel = (year, month) =>
  new Date(year, month - 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

export const RANGES = [
  ['all', 'All'],
  ['12months', '12M'],
  ['6months', '6M'],
  ['3months', '3M'],
];